import { useQuery } from '@tanstack/react-query';
import type {
  CrmBoardQuery,
  CrmDealDto,
  CrmPartyMergeFieldPick,
  CrmPartySearchHitDto,
} from '@upme/contracts';
import { CrmPartyResolveContactDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@upme/ui/components/dialog';
import { useEffect, useState } from 'react';
import { api } from '../../../app/api-client';
import { useMergeCrmParties } from '../mutations';
import { crmMergePreviewOptions, crmPartySearchOptions, crmResolvePartyOptions } from '../queries';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceDeal: CrmDealDto;
  boardFilters: CrmBoardQuery;
};

export function CrmPartyMergeDialog({ open, onOpenChange, sourceDeal, boardFilters }: Props) {
  const [searchQ, setSearchQ] = useState('');
  const [sourcePartyId, setSourcePartyId] = useState<string | null>(null);
  const [targetHit, setTargetHit] = useState<CrmPartySearchHitDto | null>(null);
  const [targetPartyId, setTargetPartyId] = useState<string | null>(null);
  const [survivorSide, setSurvivorSide] = useState<'source' | 'target'>('source');
  const [displayNamePick, setDisplayNamePick] = useState<CrmPartyMergeFieldPick>('survivor');
  const [cityPick, setCityPick] = useState<CrmPartyMergeFieldPick>('survivor');

  const resolveSource = useQuery({
    ...crmResolvePartyOptions({
      inboundChannel: sourceDeal.contact.inboundChannel,
      contactUserId: sourceDeal.contactUserId,
      ...(sourceDeal.contact.manyChatContactId
        ? { manyChatContactId: sourceDeal.contact.manyChatContactId }
        : {}),
    }),
    enabled: open,
  });

  useEffect(() => {
    if (resolveSource.data?.partyId) setSourcePartyId(resolveSource.data.partyId);
  }, [resolveSource.data?.partyId]);

  const search = useQuery({
    ...crmPartySearchOptions(searchQ, sourcePartyId ?? undefined),
    enabled: open && searchQ.trim().length >= 2,
  });

  const survivorPartyId = survivorSide === 'source' ? sourcePartyId : targetPartyId;
  const mergedPartyId = survivorSide === 'source' ? targetPartyId : sourcePartyId;

  const preview = useQuery({
    ...crmMergePreviewOptions(survivorPartyId ?? '', mergedPartyId ?? ''),
    enabled: open && Boolean(survivorPartyId && mergedPartyId && survivorPartyId !== mergedPartyId),
  });

  const mergeMut = useMergeCrmParties(boardFilters);

  async function pickTarget(hit: CrmPartySearchHitDto) {
    setTargetHit(hit);
    if (hit.partyId) {
      setTargetPartyId(hit.partyId);
      return;
    }
    const data = await api
      .get('admin/crm/parties/resolve-contact', {
        searchParams: {
          inboundChannel: hit.inboundChannel,
          contactUserId: hit.contactUserId,
          ...(hit.manyChatContactId ? { manyChatContactId: hit.manyChatContactId } : {}),
        },
      })
      .json();
    setTargetPartyId(CrmPartyResolveContactDto.parse(data).partyId);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Объединить с другим контактом</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Текущая карточка: <strong>{sourceDeal.contactLabel}</strong>. Сделки останутся отдельными, стадии
            не изменятся.
          </p>

          <div>
            <label className="mb-1 block font-medium" htmlFor="merge-search">
              Найти второй контакт
            </label>
            <input
              id="merge-search"
              className="w-full rounded-md border border-input px-3 py-2"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Username, имя или Instagram…"
            />
            {search.data?.items.length ? (
              <ul className="mt-2 max-h-40 overflow-y-auto rounded-md border">
                {search.data.items.map((hit) => (
                  <li key={`${hit.inboundChannel}-${hit.contactUserId}`}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-muted"
                      onClick={() => void pickTarget(hit)}
                    >
                      <span className="font-medium">{hit.label}</span>
                      {hit.subtitle ? (
                        <span className="ml-2 text-muted-foreground">{hit.subtitle}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {targetHit ? <p className="mt-2 text-muted-foreground">Выбрано: {targetHit.label}</p> : null}
          </div>

          {preview.data ? (
            <div className="space-y-3 rounded-md border p-3">
              <p className="font-medium">Основная карточка</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={survivorSide === 'source' ? 'default' : 'outline'}
                  onClick={() => setSurvivorSide('source')}
                >
                  Текущая
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={survivorSide === 'target' ? 'default' : 'outline'}
                  onClick={() => setSurvivorSide('target')}
                >
                  Выбранная
                </Button>
              </div>

              {preview.data.conflicts.displayName ? (
                <div>
                  <p className="mb-1 font-medium">Имя</p>
                  <select
                    className="w-full rounded-md border px-2 py-1"
                    value={displayNamePick}
                    onChange={(e) => setDisplayNamePick(e.target.value as CrmPartyMergeFieldPick)}
                  >
                    <option value="survivor">{preview.data.survivor.displayName ?? '—'}</option>
                    <option value="merged">{preview.data.merged.displayName ?? '—'}</option>
                  </select>
                </div>
              ) : null}

              {preview.data.conflicts.primaryCityId ? (
                <div>
                  <p className="mb-1 font-medium">Город</p>
                  <select
                    className="w-full rounded-md border px-2 py-1"
                    value={cityPick}
                    onChange={(e) => setCityPick(e.target.value as CrmPartyMergeFieldPick)}
                  >
                    <option value="survivor">Город основной карточки</option>
                    <option value="merged">Город второй карточки</option>
                  </select>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>
                  <p className="font-medium text-foreground">После объединения (основная)</p>
                  <p>Сделок: {preview.data.survivor.stats.deals + preview.data.merged.stats.deals}</p>
                  <p>
                    Переписок:{' '}
                    {preview.data.survivor.stats.conversations + preview.data.merged.stats.conversations}
                  </p>
                  <p>
                    Заметок:{' '}
                    {preview.data.survivor.stats.managerNotes + preview.data.merged.stats.managerNotes}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Каналы</p>
                  <p>
                    Links: {preview.data.survivor.stats.contactLinks + preview.data.merged.stats.contactLinks}
                  </p>
                  <p>
                    Inquiry MC:{' '}
                    {preview.data.survivor.stats.manyChatInquiries +
                      preview.data.merged.stats.manyChatInquiries}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={
              !survivorPartyId || !mergedPartyId || survivorPartyId === mergedPartyId || mergeMut.isPending
            }
            onClick={() => {
              if (!survivorPartyId || !mergedPartyId) return;
              mergeMut.mutate(
                {
                  survivorPartyId,
                  mergedPartyId,
                  fieldResolutions: {
                    displayName: preview.data?.conflicts.displayName ? displayNamePick : 'survivor',
                    ...(preview.data?.conflicts.primaryCityId ? { primaryCityId: cityPick } : {}),
                  },
                },
                { onSuccess: () => onOpenChange(false) },
              );
            }}
          >
            {mergeMut.isPending ? 'Объединение…' : 'Объединить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
